/**
 * 用户信息面板（P-D，综合竞品左下卡片）。左栏底部头像入口点开：
 * 工作室切换 / 头像·UUID·AccessKey(可复制) / 套餐·开通会员 / 额度电量条(只显示百分比不露绝对数) /
 * 存储空间 / 语言 / 团队管理 / 更多设置 / AI 水印 / 通知 / 模式切换 / 退出登录。
 * 数据来自 MOCK_USER；跳转用 useNavigate；真实鉴权/计费待接后端。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronRight, Copy, Crown, Droplets, HardDrive, LogOut, Settings, Sparkles, Users } from 'lucide-react';

import { PLAN_LABEL, userCreditGauge, type UserAccount } from '../../model/user';
import { clearSession } from '../../app/authApi';

interface UserPanelProps {
  user: UserAccount;
  onClose: () => void;
}

function Row({ Icon, label, right, onClick }: { Icon: typeof Users; label: string; right?: React.ReactNode; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick} className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-ui text-ink-primary transition-colors enabled:hover:bg-surface-overlay disabled:cursor-default">
      <Icon size={15} className="shrink-0 text-ink-secondary" />
      <span>{label}</span>
      <span className="ml-auto flex items-center gap-1 text-uiSm text-ink-muted">{right}</span>
    </button>
  );
}

export function UserPanel({ user, onClose }: UserPanelProps) {
  const navigate = useNavigate();
  const [watermark, setWatermark] = useState(true);
  const [copied, setCopied] = useState('');

  const g = userCreditGauge(user);
  const bonusPctAtFull = user.credits.total > 0 ? Math.round((user.credits.bonus / user.credits.total) * 100) : 0;
  const maxTrack = 100 + bonusPctAtFull;
  const baseW = (g.basePct / maxTrack) * 100;
  const bonusW = (g.bonusPct / maxTrack) * 100;
  const storagePct = user.storage.totalGB > 0 ? Math.min(100, Math.round((user.storage.usedGB / user.storage.totalGB) * 100)) : 0;

  function copy(v: string, tag: string): void {
    void navigator.clipboard?.writeText(v);
    setCopied(tag);
    setTimeout(() => setCopied(''), 1500);
  }
  function go(path: string): void {
    onClose();
    navigate(path);
  }

  return (
    <div className="flex w-[300px] flex-col gap-2 rounded-card border border-surface-edge bg-surface-overlay p-3 shadow-[0_10px_40px_rgba(0,0,0,.6)]">
      {/* 工作室 */}
      <button type="button" onClick={() => go('/team')} className="flex items-center gap-2 rounded-control bg-surface-base px-2.5 py-2 text-left">
        <span className="flex h-6 w-6 items-center justify-center rounded bg-accent/20 text-accent"><Users size={13} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] text-ink-faint">切换工作室</span>
          <span className="block truncate text-ui text-ink-primary">{user.workspaces[0]?.name ?? '默认工作室'}</span>
        </span>
        <ChevronRight size={14} className="text-ink-faint" />
      </button>

      {/* 身份 */}
      <div className="flex items-center gap-2.5 px-1 py-1">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/20 text-lg font-bold text-accent">{user.name.slice(0, 1)}</span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-ui font-semibold text-ink-primary">{user.name}</div>
          <div className="flex items-center gap-1 text-[11px] text-ink-faint">
            <span className="truncate">UUID {user.uuid.slice(0, 8)}…</span>
            <button type="button" onClick={() => copy(user.uuid, 'uuid')} className="hover:text-accent" aria-label="复制 UUID"><Copy size={11} /></button>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-ink-faint">
            <span className="truncate">AK {user.accessKey}</span>
            <button type="button" onClick={() => copy(user.accessKey, 'ak')} className="hover:text-accent" aria-label="复制 AccessKey"><Copy size={11} /></button>
          </div>
        </div>
        <button type="button" onClick={() => go('/team')} className="shrink-0 rounded-control border border-surface-line px-2 py-1 text-uiSm text-ink-secondary hover:text-accent">+ 创建团队</button>
      </div>
      {copied !== '' && <p className="text-center text-[11px] text-state-ok">已复制</p>}

      {/* 套餐 */}
      <div className="flex items-center gap-2 rounded-control bg-accent/10 px-2.5 py-2">
        <Crown size={15} className="text-accent" />
        <span className="text-ui font-semibold text-ink-primary">{PLAN_LABEL[user.plan]}</span>
        <button type="button" onClick={() => go('/pricing')} className="ml-auto rounded-control bg-accent px-2.5 py-1 text-uiSm font-semibold text-accent-ink">开通会员</button>
      </div>
      <button type="button" onClick={() => {}} className="flex items-center gap-1.5 px-1 text-uiSm text-ink-muted hover:text-ink-primary">
        <Sparkles size={12} className="text-accent" /> 活动权益：Seedream 4.5 限时 5 折 · 有效期 1 天
        <ChevronRight size={12} className="ml-auto" />
      </button>

      {/* 额度（电量式，只显示百分比） */}
      <div className="rounded-control border border-surface-line bg-surface-base/40 p-2.5">
        <div className="flex items-center justify-between text-uiSm">
          <span className="text-ink-faint">额度</span>
          <span className="font-num text-accent">剩余 {g.totalPct}%</span>
        </div>
        <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-surface-base">
          <div className="h-full bg-accent" style={{ width: `${baseW}%` }} />
          <div className="h-full bg-state-ok" style={{ width: `${bonusW}%` }} />
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-ink-faint">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-accent" />基础</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-state-ok" />赠送</span>
          <button type="button" onClick={() => go('/pricing')} className="ml-auto text-accent hover:underline">充值</button>
          <span>·</span>
          <button type="button" onClick={() => {}} className="hover:text-ink-primary">消耗顺序</button>
        </div>
      </div>

      {/* 存储空间 */}
      <div className="rounded-control border border-surface-line bg-surface-base/40 p-2.5">
        <div className="flex items-center justify-between text-uiSm">
          <span className="flex items-center gap-1.5 text-ink-faint"><HardDrive size={12} /> 存储空间</span>
          <button type="button" onClick={() => go('/assets')} className="text-accent hover:underline">管理资产</button>
        </div>
        <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-surface-base">
          <div className="h-full bg-accent/70" style={{ width: `${storagePct}%` }} />
        </div>
        <div className="mt-1 text-[11px] text-ink-faint">{user.storage.usedGB}G / {user.storage.totalGB}G</div>
      </div>

      {/* 功能行 */}
      <div className="mt-1 flex flex-col">
        <Row Icon={Users} label="团队管理" right={<ChevronRight size={14} />} onClick={() => go('/team')} />
        <Row Icon={Settings} label="更多设置" right={<ChevronRight size={14} />} onClick={() => go('/settings')} />
        <Row Icon={Droplets} label="AI 水印设置" right={
          <button type="button" onClick={() => setWatermark((v) => !v)} className={`h-4 w-8 rounded-full transition-colors ${watermark ? 'bg-accent' : 'bg-surface-line'}`}>
            <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${watermark ? 'translate-x-4' : ''}`} />
          </button>
        } />
        <Row Icon={Bell} label="通知" right={user.notifications > 0 ? <span className="rounded-full bg-state-danger px-1.5 text-[10px] text-white">{user.notifications}</span> : undefined} onClick={() => {}} />
        <Row Icon={LogOut} label="退出登录" onClick={() => { clearSession(); go('/login'); }} />
      </div>
    </div>
  );
}
